import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
  SelectChangeEvent,
} from '@mui/material';
import { X as DeleteIcon } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { Link as RouterLink } from 'react-router-dom';

interface Strand {
  _id: string;
  name: string;
}

// ✅ FIX: Define the correct shape of published strand data
interface PublishedStrand {
  strandId: string | { _id: string; name: string }; // Could be populated or just ID
  shortDescription?: string;
  publishedDate?: string;
}

interface PublishBandModalProps {
  open: boolean;
  onClose: () => void;
  bandId: string;
  bandName: string;
  onPublish: (strandId: string, shortDescription: string) => void;
}

const PublishBandModal: React.FC<PublishBandModalProps> = ({
  open,
  onClose,
  bandId,
  bandName,
  onPublish,
}) => {
  const [strands, setStrands] = useState<Strand[]>([]);
  const [selectedStrand, setSelectedStrand] = useState<string>('');
  // ✅ FIX: Change to array of objects instead of strings
  const [publishedStrands, setPublishedStrands] = useState<PublishedStrand[]>([]);
  const [shortDescription, setShortDescription] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        const [strandsResponse, bandResponse] = await Promise.all([
          axiosInstance.get<Strand[]>('/api/strands'),
          axiosInstance.get(`/api/bandrecommendations/${bandId}`),
        ]);
        
        console.log('🔍 Band data from API:', bandResponse.data);
        console.log('🔍 Published strands:', bandResponse.data.publishedTo);
        
        setStrands(strandsResponse.data);
        // ✅ FIX: Handle the correct data structure
        setPublishedStrands(bandResponse.data.publishedTo || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to fetch data. Please try again.');
      }
    };
    if (open) {
      fetchData();
    }
  }, [bandId, open]);

  const handleStrandChange = (event: SelectChangeEvent<string>) => {
    setSelectedStrand(event.target.value as string);
  };

  const handlePublish = () => {
    onPublish(selectedStrand, shortDescription);
    // Don't close immediately - let the parent handle the state update
    setSelectedStrand('');
    setShortDescription('');
  };

  const handleUnpublish = async (strandId: string) => {
    console.log('🗑️ Attempting to unpublish strand:', strandId);
    console.log('🗑️ Current published strands:', publishedStrands);
    
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(
        `/api/bandrecommendations/${bandId}/unpublish/${strandId}`
      );
      console.log('✅ Unpublish response:', response);
      
      // ✅ FIX: Filter by strandId property (handle both cases)
      setPublishedStrands((prevStrands) => {
        const filtered = prevStrands.filter((pub) => {
          const pubStrandId = typeof pub.strandId === 'string' ? pub.strandId : pub.strandId._id;
          return pubStrandId !== strandId;
        });
        console.log('🔄 Updated published strands after filter:', filtered);
        return filtered;
      });
    } catch (error: any) {
      console.error('❌ Error unpublishing band:', error);
      setError(
        error.response?.data?.message || 'Failed to unpublish. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ FIX: Get list of published strand IDs for filtering (handle both cases)
  const publishedStrandIds = publishedStrands.map(pub => 
    typeof pub.strandId === 'string' ? pub.strandId : pub.strandId._id
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Publication Status - {bandName}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Currently Published Section */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom sx={{ color: 'yellow' }}>
            Currently Published To
          </Typography>
          <List>
            {publishedStrands.length > 0 ? (
              publishedStrands.map((publishedStrand) => {
                // ✅ FIX: Handle both populated and non-populated strandId
                const strandId = typeof publishedStrand.strandId === 'string' 
                  ? publishedStrand.strandId 
                  : publishedStrand.strandId._id;
                
                const strandName = typeof publishedStrand.strandId === 'string'
                  ? strands.find((s) => s._id === publishedStrand.strandId)?.name
                  : publishedStrand.strandId.name;

                return (
                  <ListItem
                    key={strandId}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="unpublish"
                        onClick={() => handleUnpublish(strandId)}
                        disabled={loading}
                      >
                        <DeleteIcon className="h-4 w-4" />
                      </IconButton>
                    }
                  >
                    <ListItemText 
                      primary={strandName || `Unknown Strand (${strandId})`}
                      secondary={publishedStrand.shortDescription || 'No description'}
                    />
                  </ListItem>
                );
              })
            ) : (
              <ListItem>
                <ListItemText primary="Not published to any strands" />
              </ListItem>
            )}
          </List>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" gutterBottom sx={{ color: 'yellow' }}>
            Publish to New Strand
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component={RouterLink}
            to="/strands/new"
          >
            Create New Strand
          </Button>
        </Box>

        <FormControl fullWidth margin="normal">
          <InputLabel>Select Strand</InputLabel>
          <Select
            value={selectedStrand}
            onChange={handleStrandChange}
            label="Select Strand"
          >
            {strands.map((strand) => (
              <MenuItem
                key={strand._id}
                value={strand._id}
                // ✅ FIX: Check against publishedStrandIds
                disabled={publishedStrandIds.includes(strand._id)}
              >
                {strand.name}
                {publishedStrandIds.includes(strand._id) && ' (Already published)'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          margin="normal"
          label="Short Description"
          multiline
          rows={4}
          value={shortDescription}
          onChange={(e) => setShortDescription(e.target.value)}
          helperText="Optional: Provide a short description useful for programmers. This will be public."
          placeholder="Enter a short description..."
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          onClick={handlePublish}
          variant="contained"
          color="primary"
          disabled={!selectedStrand || loading}
        >
          Publish to Selected Strand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PublishBandModal;